import { selectById } from "@pod-point/dom-ops";
import { Delegate } from "dom-delegate";
import serialize from "form-serialize";
import { trackEvent } from "./helpers";

let instances = [];

class TrackForm {
    /**
     * Track form submits.
     *
     * @param  {HTMLElement} form
     * @return {void}
     */
    constructor(form) {
        this.requestInProgress = false;
        this.form = form;
        this.eventName = form.getAttribute("data-track-event-name") || "MQL Submitted";

        this.analyticsFields = {
            track: ["conversion", "industry", "interestReason", "label", "parkingSpaces", "locale"],
            identify: ["firstName", "lastName", "email", "phone", "company"],
        };

        this.bindEvents();
    }

    static getExcludedFields() {
        return ["action", "redirect", "retURL", "oid"];
    }

    /**
     * Filters and formats the form fields valid for the analytics events.
     *
     * @param {Array} input
     * @return {Object}
     */
    getAnalyticsData(input) {
        return Object.entries(input).reduce(
            (analyticsData, [key, value]) => {
                if (this.analyticsFields.track.includes(key)) {
                    analyticsData.track[key] = value; // eslint-disable-line no-param-reassign
                } else if (this.analyticsFields.identify.includes(key)) {
                    analyticsData.identify[key] = value; // eslint-disable-line no-param-reassign
                }

                return analyticsData;
            },
            {
                identify: {},
                track: {},
            },
        );
    }

    /**
     * Bind form events.
     *
     * @return {void}
     */
    bindEvents() {
        this.listener = new Delegate(this.form);

        this.listener.on("submit", (event) => {
            event.preventDefault();

            if (!this.requestInProgress && this.isValid()) {
                this.trackForm();
            }
        });
    }

    /**
     * Tracks the form submit event.
     *
     * @return {void}
     */
    trackForm() {
        const formFields = serialize(this.form, {
            hash: true,
            serializer: TrackForm.formSerialiser,
        });

        const SEOmetadata = JSON.parse(document.body.getAttribute("data-custom-meta") || "{}");

        const analyticsData = this.getAnalyticsData(formFields);

        Object.keys(SEOmetadata).forEach((key) => {
            if (this.analyticsFields.track.includes(key)) {
                analyticsData.track[key] = SEOmetadata[key];
            }
            if (this.analyticsFields.identify.includes(key)) {
                analyticsData.identify[key] = SEOmetadata[key];
            }
        });

        trackEvent(this.eventName, analyticsData.track);
        this.form.submit();
    }

    /**
     * Serialise a form as json.
     *
     * @param  {Object} result
     * @param  {String} key
     * @param  {String} value
     * @return {Object}
     */
    static formSerialiser(result, key, value) {
        const field = selectById(key);

        let newKey = field && field.dataset.trackingName ? field.dataset.trackingName : key;

        switch (newKey) {
            case "zip":
                newKey = "postalCode";

                break;
            case "first_name":
                newKey = "firstName";

                break;
            case "last_name":
                newKey = "lastName";

                break;
            case "phoneNumber":
                newKey = "phone";

                break;
            case "country":
                newKey = "locale";

                break;
            default:
        }

        result[newKey] = value; // eslint-disable-line no-param-reassign

        return result;
    }

    /**
     * Check if the form input is valid.
     *
     * @return {Boolean}
     */
    isValid() {
        const formFields = this.form.querySelectorAll("input, textarea, select");
        const formFieldsLength = formFields.length;

        for (let i = 0; i < formFieldsLength; i += 1) {
            const formField = formFields[i];

            // Check if the field is not excluded, is required and is empty
            if (
                TrackForm.getExcludedFields().indexOf(formField.name) === -1 &&
                formField.getAttribute("required") !== null &&
                formField.value.trim() === ""
            ) {
                // If so, it fails validation
                return false;
            }
        }

        return true;
    }
}

export default {
    init: (form) => {
        instances.push(new TrackForm(form));
    },

    destroy: () => {
        instances = [];
    },
};
